<template>
    <div v-if="aperto" class="dd-overlay" @click.self="$emit('chiudi')">
        <div class="dd-panel" role="dialog" aria-modal="true" aria-label="Elenco progetti per stato">
            <div class="dd-head">
                <div>
                    <div class="text-muted small">Dashboard Manager · Drill-down Stato / Pipeline</div>
                    <h3 class="dd-title">Progetti in stato: {{ statoLabel }}</h3>
                    <div class="text-muted font-size-sm" v-if="riepilogoFiltri">
                        Filtri Dashboard applicati: {{ riepilogoFiltri }}
                    </div>
                </div>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="$emit('chiudi')"
                        aria-label="Chiudi">Chiudi</button>
            </div>

            <div class="dd-body">
                <div v-if="caricamento" class="text-center py-4">
                    <div class="spinner-border" role="status"><span class="visually-hidden">Caricamento…</span></div>
                </div>

                <BlockState v-else-if="errore" stato="errore"
                            dettaglio="Elenco progetti non disponibile." />

                <div v-else-if="conflitto" class="alert alert-warning mb-0">
                    Lo stato selezionato è diverso dallo stato già filtrato nella Dashboard:
                    nessun progetto corrisponde all'intersezione richiesta.
                </div>

                <p v-else-if="!progetti.length" class="text-center text-muted mb-0">
                    Nessun progetto corrisponde ai filtri applicati.
                </p>

                <table v-else class="table table-sm font-size-sm mb-0 align-middle">
                    <thead>
                        <tr>
                            <th scope="col">ID</th>
                            <th scope="col">Codice</th>
                            <th scope="col">Titolo</th>
                            <th scope="col">Data evento</th>
                            <th scope="col">Stato</th>
                            <th scope="col">Cliente</th>
                            <th scope="col">Località</th>
                            <th scope="col">Account</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="p in progetti" :key="p.id_progetto">
                            <td>{{ p.id_progetto }}</td>
                            <td>{{ p.codice }}</td>
                            <td>{{ p.titolo }}</td>
                            <td>{{ p.data_evento }}</td>
                            <td>{{ p.stato_id }}</td>
                            <td>{{ p.cliente }}</td>
                            <td>{{ p.localita }}</td>
                            <td>{{ p.account }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="dd-foot">
                <span class="text-muted font-size-sm" v-if="!caricamento && !errore">
                    Totale: {{ totale }} · pagina {{ pagina }} di {{ pagine || 1 }}
                </span>
                <span class="ms-auto d-flex gap-2">
                    <button type="button" class="btn btn-sm btn-outline-secondary"
                            :disabled="pagina <= 1 || caricamento" @click="vai(pagina - 1)">Precedente</button>
                    <button type="button" class="btn btn-sm btn-outline-secondary"
                            :disabled="!pagine || pagina >= pagine || caricamento" @click="vai(pagina + 1)">Successiva</button>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
/* B2.2A.1-R - Drawer READ-ONLY: elenco dei progetti che compongono un dato
   della Dashboard. Nessuna azione CRUD, storico, log, task o bulk.
   Non e' una navigazione: chiudere il drawer riporta alla Dashboard
   esattamente nello stato precedente (nessun ricaricamento, nessun reset). */
import BlockState from "./BlockState.vue";

const API = "theb/dashboard/manager/drilldown/projects";

export default {
    name: "DrillDownDrawer",
    components: { BlockState },
    props: {
        aperto: { type: Boolean, default: false },
        /* contesto filtri applicato nella Dashboard (filtriEspliciti del padre) */
        contesto: { type: Object, default: null },
        /* stato cliccato: id + label, gia' risolti dal padre */
        stato: { type: Object, default: null },
    },
    data() {
        return { progetti: [], totale: 0, pagina: 1, pagine: 0, caricamento: false, errore: false, conflitto: false };
    },
    computed: {
        statoLabel() {
            if (!this.stato) return "";
            return this.stato.descrizione || ("#" + this.stato.state_id);
        },
        /* Riepilogo leggibile del contesto: nessun id inventato. */
        riepilogoFiltri() {
            const c = this.contesto || {};
            const out = [];
            if (c.date_from || c.date_to) out.push("periodo " + (c.date_from || "…") + " → " + (c.date_to || "…"));
            if (c.account_id) out.push("account #" + c.account_id);
            if (c.client_type) out.push("cliente " + c.client_type + (c.client_id ? " #" + c.client_id : ""));
            if (c.location_id) out.push("località #" + c.location_id);
            if (c.supplier_id) out.push("fornitore #" + c.supplier_id);
            if (c.tipology_id) out.push("tipologia #" + c.tipology_id);
            if (c.state_id) out.push("stato #" + c.state_id);
            return out.join(" · ");
        },
    },
    watch: {
        /* B2.2A.1-R HOTFIX: UNICO percorso automatico verso ricarica(1).
           Il watcher "stato" e' stato rimosso: il padre cambia `aperto` e `stato`
           nello STESSO tick (this.drilldown = { stato: {...} }), quindi i due
           watcher fratelli finivano nella stessa coda di flush e lanciavano due
           GET identici per ogni click. "aperto" copre da solo sia l'apertura
           (ricambio stato) sia la chiusura; "stato" resta una prop puramente
           presentazionale (etichetta del titolo).
           Nota: nessun debounce/abort/sequence guard -> non necessari, una
           sola richiesta per apertura. */
        aperto(v) { if (v) this.ricarica(1); },
    },
    methods: {
        vai(p) { this.ricarica(p); },
        ricarica(pagina) {
            if (!this.stato || !this.stato.state_id) { this.errore = true; this.progetti = []; return; }
            this.pagina = pagina;
            this.caricamento = true; this.errore = false; this.conflitto = false;
            const args = Object.assign({}, this.contesto || {}, {
                derived_state_id: this.stato.state_id,
                page: pagina,
            });
            global.utils.get(API, args, (response) => {
                this.caricamento = false;
                if (!response || String(response.esito) !== "0") { this.errore = true; this.progetti = []; this.totale = 0; return; }
                const d = response.data || {};
                this.conflitto = !!d.conflitto;
                this.progetti = Array.isArray(d.progetti) ? d.progetti : [];
                /* B2.2A.1-R HOTFIX: `intval` vive solo come this.intval (created()).
                   La chiamata libera lanciava ReferenceError DENTRO questo callback:
                   la promise rigettava e utils.js:107-108 mostrava l'alert rosso
                   "Si è verificato un errore", mentre this.progetti (gia' assegnato
                   alla riga precedente) rendeva le 2 righe e totale/pagine restavano 0. */
                this.totale = this.intval(d.totale);
                this.pagine = this.intval(d.pagine);
                /* allinea la pagina alla risposta server: evita che la UI mostri una
                   pagina diversa in caso di clamp lato backend. */
                this.pagina = this.intval(d.page) || 1;
            }, false);
        },
    },
    created() {
        this.intval = (v) => parseInt(v, 10) || 0;
    },
};
</script>

<style scoped>
.dd-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 1080; display: flex; justify-content: flex-end; }
.dd-panel { background: #fff; width: min(1100px, 96vw); height: 100%; display: flex; flex-direction: column; box-shadow: -4px 0 18px rgba(0,0,0,.25); }
.dd-head { display: flex; align-items: flex-start; gap: 1rem; padding: 1rem 1.25rem; border-bottom: 1px solid #e3e6f0; }
.dd-title { font-size: 1.05rem; margin: .15rem 0 .25rem; }
.dd-body { flex: 1; overflow: auto; padding: 1rem 1.25rem; }
.dd-foot { display: flex; align-items: center; padding: .75rem 1.25rem; border-top: 1px solid #e3e6f0; }
</style>

<style>
/* affordance discreta sulle righe di stato drill-downabili */
.drilldown-stato-row { cursor: pointer; }
.drilldown-stato-row:hover { background: #f1f3f9; }
</style>